export type ProductArea = "local" | "competitive";

export type ActionBriefSourceOpportunity = {
  title: string;
  summary?: string;
  recommendation?: string;
  category?: string;
  intent?: string;
  priority?: string;
  evidence?: string[];
  competitors?: string[];
  location?: string;
};

export type ActionBrief = {
  id: string;
  account_id: string;
  run_id: string | null;
  product_area: ProductArea;

  opportunity_key: string;
  opportunity_fingerprint: string;

  action_stage: number;
  action_type: string;

  title: string;
  recommended_action: string | null;
  why_this_matters: string | null;

  ai_writing_prompt: string;
  content_brief: Record<string, unknown>;
  suggested_structure: Record<string, unknown>;
  guardrails: unknown[];

  source_context: Record<string, unknown>;

  generation_status: string;
  generation_reason: string;

  model_provider: string | null;
  model_name: string | null;
  prompt_template_version: string | null;

  input_tokens: number | null;
  output_tokens: number | null;
  estimated_cost: number | null;

  generated_by: string | null;

  is_active: boolean;
  marked_complete_at: string | null;
  completed_by: string | null;

  created_at: string;
  updated_at: string;
};

export type GenerateActionBriefInput = {
  accountId: string;
  runId?: string | null;
  productArea: ProductArea;
  opportunityKey?: string;
  opportunityFingerprint?: string;
  opportunity: ActionBriefSourceOpportunity;
  accountContext?: {
    accountName?: string;
    category?: string;
    location?: string;
    competitors?: string[];
    differentiators?: string[];
    brandGuidance?: {
      id?: string;
      updatedAt?: string;
      rawGuidance?: string;
      brandVoiceSummary?: string;
      toneDo?: string[];
      toneDont?: string[];
      preferredTerms?: string[];
      bannedTerms?: string[];
      approvedClaims?: string[];
      claimsToAvoid?: string[];
      audienceNotes?: string;
      positioningNotes?: string;
      differentiators?: string[];
      ctaPreferences?: string;
      complianceNotes?: string;
      oeGuardrails?: string;
    };
  };
};

export const COMPETITIVE_COMPLETION_MEMORY_DAYS = 90;

export type CompetitiveSemanticIdentityInput = {
  productArea?: ProductArea | string | null;
  actionType?: string | null;
  action_type?: string | null;
  title?: string | null;
  recommendationType?: string | null;
  entityName?: string | null;
  opportunity?: Partial<ActionBriefSourceOpportunity> | null;
  accountContext?: Pick<NonNullable<GenerateActionBriefInput["accountContext"]>, "competitors" | "category"> | null;
  sourceContext?: Record<string, unknown> | null;
};

function normalizeSemanticToken(value: unknown): string {
  return String(value || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "")
    .trim();
}

function firstString(values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
}

export function normalizeComparedEntity(value: unknown): string {
  return normalizeSemanticToken(value);
}

function extractSemanticEntity(input: CompetitiveSemanticIdentityInput): string | null {
  const source = input.sourceContext || {};
  const opportunity = input.opportunity || {};
  const candidates = [
    input.entityName,
    source.comparedEntity,
    source.compared_entity,
    ...stringList(opportunity.competitors),
    ...(stringList(input.accountContext?.competitors).length === 1 ? stringList(input.accountContext?.competitors) : []),
  ];
  const structured = firstString(candidates);
  if (structured) return normalizeComparedEntity(structured);

  const title = String(input.title || opportunity.title || "");
  const match = title.match(/(?:against|vs\.?|versus|compared?\s+(?:with|to))\s+([^|,:;]+)/i);
  return match ? normalizeComparedEntity(match[1]) : null;
}

export function getCompetitiveActionFamily(input: CompetitiveSemanticIdentityInput): string | null {
  if (input.productArea && input.productArea !== "competitive") return null;

  const source = input.sourceContext || {};
  const opportunity = input.opportunity || {};
  const values = [
    input.actionType,
    input.action_type,
    input.recommendationType,
    opportunity.intent,
    opportunity.category,
    source.activationType,
    source.activation_type,
    source.recommendationType,
    source.recommendation_type,
  ].map(normalizeSemanticToken);
  const title = normalizeSemanticToken(input.title || opportunity.title);
  const text = [title, normalizeSemanticToken(opportunity.recommendation), normalizeSemanticToken(opportunity.summary)].join("|");

  if (/(entity|schema|cleanup|review|mapping|alias|suppression)/.test(values.join("|")) || /(entity|schema|cleanup|review|mapping|alias|suppression)/.test(title)) {
    return null;
  }

  const comparisonSignals = [
    "generatecomparisonbrief",
    "comparisonbrief",
    "comparisons",
    "comparison",
    "alternativesreplacements",
    "alternatives",
    "replacements",
  ];
  if (values.some((value) => comparisonSignals.includes(value)) || /(compare|comparison|alternative|replacement|proofagainst|closegapvs|closegapagainst)/.test(text)) {
    return "competitive_comparison_proof";
  }

  return null;
}

export function getCompetitiveSemanticIdentity(input: CompetitiveSemanticIdentityInput): { comparedEntity: string | null; actionFamily: string | null; semanticKey: string | null } {
  const comparedEntity = extractSemanticEntity(input);
  const actionFamily = getCompetitiveActionFamily(input);
  return {
    comparedEntity,
    actionFamily,
    semanticKey: comparedEntity && actionFamily ? `${comparedEntity}|${actionFamily}` : null,
  };
}

export function isWithinCompetitiveCompletionMemory(markedCompleteAt: string | null | undefined, now = new Date()): boolean {
  if (!markedCompleteAt) return false;
  const completedAt = Date.parse(markedCompleteAt);
  if (!Number.isFinite(completedAt)) return false;
  return completedAt >= now.getTime() - COMPETITIVE_COMPLETION_MEMORY_DAYS * 24 * 60 * 60 * 1000;
}

export function getCompetitiveOpportunitySemanticIdentity(item: {
  competitor: string;
  prompt_focus?: string;
  prompt_focus_label?: string;
}) {
  return getCompetitiveSemanticIdentity({
    productArea: "competitive",
    entityName: item.competitor,
    title: `Close gap vs ${item.competitor}`,
    opportunity: {
      title: `Close gap vs ${item.competitor}`,
      category: item.prompt_focus_label || item.prompt_focus,
      intent: item.prompt_focus,
      competitors: [item.competitor],
    },
  });
}

export function filterCompetitiveOpportunitiesByCompletionMemory<T extends {
  competitor: string;
  prompt_focus?: string;
  prompt_focus_label?: string;
}>(items: T[], completedSemanticKeys: Set<string>): T[] {
  return items.filter((item) => {
    const identity = getCompetitiveOpportunitySemanticIdentity(item);
    return !identity.semanticKey || !completedSemanticKeys.has(identity.semanticKey);
  });
}
export function buildOpportunityKey(input: GenerateActionBriefInput): string {
  const parts = [
    input.productArea,
    input.accountContext?.category,
    input.accountContext?.location,
    input.opportunity.category,
    input.opportunity.intent,
    input.opportunity.title,
  ]
    .filter(Boolean)
    .map((part) =>
      String(part)
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
    );

  return parts.join("|").slice(0, 500);
}
export async function buildOpportunityFingerprint(
  input: GenerateActionBriefInput
): Promise<string> {
  const payload = JSON.stringify({
    productArea: input.productArea,
    opportunity: input.opportunity,
    accountContext: input.accountContext,
  });

  const encoder = new TextEncoder();
  const data = encoder.encode(payload);
  const hashBuffer = await crypto.subtle.digest("SHA-256", data);

  return Array.from(new Uint8Array(hashBuffer))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}
